(() => {
  const COMPONENT_NAME = "animated-search-text";
  /* The desktop field's breakpoint, as in the header's own layout sync. */
  const DESKTOP_QUERY = "(min-width: 768px)";

  if (customElements.get(COMPONENT_NAME)) return;

  /* classList writes normalise the attribute's whitespace, so compare the
     class tokens, not the strings. */
  const sameValue = (name, a, b) => {
    if (name !== "class" || a === null || b === null) return a === b;

    const tokens = (value) =>
      value.split(/\s+/).filter(Boolean).sort().join(" ");

    return tokens(a) === tokens(b);
  };

  /* Header attributes change constantly: every layout sync adds and then
     removes header--no-motion within one task. Only a change that stuck can
     move the search in or out of view. */
  const hasNetChange = (records) => {
    const before = new Map();

    for (const { target, attributeName, oldValue } of records) {
      const values = before.get(target) || new Map();

      if (!values.has(attributeName)) values.set(attributeName, oldValue);
      before.set(target, values);
    }

    for (const [target, values] of before) {
      for (const [name, oldValue] of values) {
        const value = target.getAttribute(name);

        if (!sameValue(name, value, oldValue)) return true;
      }
    }

    return false;
  };

  class AnimatedSearchText extends HTMLElement {
    #overlay = null;
    #typewriterElement = null;
    #input = null;
    #field = null;
    #desktopForm = null;
    #phrases = [];
    #fallbackPlaceholder = "";
    #phraseIndex = 0;
    #charIndex = 0;
    #isDeleting = false;
    #timeoutId = null;
    #isRunning = false;
    #layoutObserver = null;
    #intersectionObserver = null;
    #measuredFor = "";

    static TYPE_SPEED_MS = 80;
    static DELETE_SPEED_MS = 50;
    static PAUSE_MS = 1200;

    connectedCallback() {
      this.#overlay = this.querySelector("[data-animated-search-text-overlay]");
      this.#typewriterElement = this.querySelector(
        "[data-animated-search-text-typewriter]",
      );
      this.#phrases = this.#parsePhrases();
      this.#fallbackPlaceholder = this.dataset.fallbackPlaceholder || "";
      // The field hosts the phrase-width probe and receives
      // --header-search-field-min-width; without it the probe measures on
      // document.body and long phrases clip inside the pill.
      this.#field = this.parentElement;
      this.#input = this.#field?.querySelector(
        "[data-animated-search-text-input]",
      );
      this.#desktopForm = this.closest(".header__search--desktop");

      // Bailing out here leaves the markup as rendered: overlay hidden, real
      // placeholder on the input.
      if (
        !this.#overlay ||
        !this.#typewriterElement ||
        this.#phrases.length === 0
      )
        return;

      // Reduced motion gets the non-animated placeholder — the merchant's
      // fallback, or the theme default — and never the overlay.
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        if (this.#input && this.#fallbackPlaceholder) {
          this.#input.placeholder = this.#fallbackPlaceholder;
        }
        return;
      }

      if (this.#input) {
        // The overlay takes over from here, so the input must not paint its
        // own placeholder underneath it.
        this.#input.placeholder = "";
        this.#input.addEventListener("focus", this.#syncVisibility);
        this.#input.addEventListener("blur", this.#syncVisibility);
        this.#input.addEventListener("input", this.#syncVisibility);
      }

      this.#observeLayoutVisibility();
      this.#syncMinWidth();

      // The connect-time measurement reads whatever font is painted at that
      // moment; a webfont that lands afterwards renders the phrases wider
      // than the reserved width and clips the typewriter. Re-measure when
      // the fonts settle and when the viewport changes — the matching
      // removeEventListener is already in disconnectedCallback.
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          // Measuring can be what requests the webfont, so the first pass
          // may have read the fallback font under the same font status the
          // cache now holds. Measure again regardless.
          this.#measuredFor = "";
          this.#syncMinWidth();
        });
      }

      window.addEventListener("resize", this.#syncMinWidth);

      this.#syncVisibility();
      this.#start();
    }

    disconnectedCallback() {
      this.#stop();
      this.#layoutObserver?.disconnect();
      this.#layoutObserver = null;
      this.#intersectionObserver?.disconnect();
      this.#intersectionObserver = null;
      window.removeEventListener("resize", this.#syncMinWidth);
      this.#input?.removeEventListener("focus", this.#syncVisibility);
      this.#input?.removeEventListener("blur", this.#syncVisibility);
      this.#input?.removeEventListener("input", this.#syncVisibility);
      this.#clearMinWidth();
    }

    #parsePhrases() {
      const rawValue = this.dataset.phrases;
      if (!rawValue) return [];

      try {
        const parsed = JSON.parse(rawValue);
        if (!Array.isArray(parsed)) return [];

        return parsed
          .flatMap((phrase) => String(phrase).split(/\r?\n/))
          .map((phrase) => phrase.trim())
          .filter(Boolean)
          .slice(0, 3);
      } catch {
        return [];
      }
    }

    #start() {
      if (this.#isRunning || this.#overlay?.hasAttribute("hidden")) return;

      this.#isRunning = true;
      this.#tick();
    }

    #stop() {
      this.#isRunning = false;

      if (this.#timeoutId) {
        window.clearTimeout(this.#timeoutId);
        this.#timeoutId = null;
      }
    }

    #tick = () => {
      if (!this.#isRunning || this.#overlay?.hasAttribute("hidden")) {
        this.#stop();
        return;
      }

      const phrase = this.#phrases[this.#phraseIndex];

      if (!this.#isDeleting) {
        this.#charIndex += 1;
        this.#typewriterElement.textContent = phrase.slice(0, this.#charIndex);

        if (this.#charIndex >= phrase.length) {
          this.#timeoutId = window.setTimeout(() => {
            this.#isDeleting = true;
            this.#tick();
          }, AnimatedSearchText.PAUSE_MS);
          return;
        }

        this.#timeoutId = window.setTimeout(
          this.#tick,
          AnimatedSearchText.TYPE_SPEED_MS,
        );
        return;
      }

      this.#charIndex -= 1;
      this.#typewriterElement.textContent = phrase.slice(0, this.#charIndex);

      if (this.#charIndex <= 0) {
        this.#isDeleting = false;
        this.#phraseIndex = (this.#phraseIndex + 1) % this.#phrases.length;
      }

      this.#timeoutId = window.setTimeout(
        this.#tick,
        AnimatedSearchText.DELETE_SPEED_MS,
      );
    };

    #syncVisibility = () => {
      const shouldHide = this.#shouldHide();
      this.#overlay?.toggleAttribute("hidden", shouldHide);
      this.#overlay?.setAttribute("aria-hidden", String(shouldHide));

      if (shouldHide) {
        this.#stop();
        return;
      }

      if (
        !this.#isRunning &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        this.#start();
      }
    };

    #shouldHide() {
      if (!this.#isLayoutVisible()) return true;

      return Boolean(
        this.#input &&
        (document.activeElement === this.#input || this.#input.value !== ""),
      );
    }

    #isLayoutVisible() {
      const form = this.closest("form");
      const probe = this.#input || form || this.#overlay;

      if (!probe) return false;

      if (probe.closest("[hidden]")) return false;

      if (typeof probe.checkVisibility === "function") {
        return probe.checkVisibility();
      }

      const style = window.getComputedStyle(probe);

      return style.display !== "none" && style.visibility !== "hidden";
    }

    #observeLayoutVisibility() {
      const form = this.closest("form");
      const header = this.closest("[data-header]");
      const stacked = this.closest("[data-header-stacked-search]");
      const targets = [form, stacked, header].filter(Boolean);

      if (targets.length > 0) {
        this.#layoutObserver = new MutationObserver((records) => {
          if (!hasNetChange(records)) return;

          this.#syncVisibility();
          this.#syncMinWidth();
        });

        for (const target of targets) {
          this.#layoutObserver.observe(target, {
            attributes: true,
            attributeFilter: ["hidden", "class"],
            attributeOldValue: true,
          });
        }
      }

      // Catches CSS-only hide/show (e.g. display:none from layout classes).
      if (typeof IntersectionObserver === "function" && form) {
        this.#intersectionObserver = new IntersectionObserver(() => {
          this.#syncVisibility();
          this.#syncMinWidth();
        });
        this.#intersectionObserver.observe(form);
      }
    }

    #syncMinWidth = () => {
      if (!this.#desktopForm || !this.#overlay || !this.#typewriterElement) {
        return;
      }

      if (this.#phrases.length === 0) return;

      // Measuring clones the overlay and forces style and layout for the
      // whole page. The desktop field is not displayed below 768px, and the
      // widths only change with the viewport or the webfont, so measure once
      // per pair. The resize listener measures on crossing to desktop.
      if (!window.matchMedia(DESKTOP_QUERY).matches) return;

      const measureFor = `${window.innerWidth}|${document.fonts?.status}`;

      if (measureFor === this.#measuredFor) return;

      const textWidth = this.#measureLongestOverlayWidth();

      if (textWidth <= 0) return;

      this.#measuredFor = measureFor;

      this.#field?.style.setProperty(
        "--header-search-field-min-width",
        `${textWidth}px`,
      );

      const formStyles = window.getComputedStyle(this.#desktopForm);
      const paddingInline =
        (Number.parseFloat(formStyles.paddingInlineStart) || 0) +
        (Number.parseFloat(formStyles.paddingInlineEnd) || 0);
      const gap =
        Number.parseFloat(formStyles.columnGap || formStyles.gap) || 0;
      const iconWidth = this.#getSearchIconWidth();

      this.#desktopForm.style.setProperty(
        "--header-search-min-width",
        `${Math.ceil(paddingInline + iconWidth + gap + textWidth)}px`,
      );
    };

    #getSearchIconWidth() {
      const icon = this.#desktopForm?.querySelector(".header__search-icon");

      if (!icon) return 20;

      const measuredWidth = icon.getBoundingClientRect().width;

      if (measuredWidth > 0) return measuredWidth;

      const iconStyles = window.getComputedStyle(icon);

      return (
        Number.parseFloat(iconStyles.getPropertyValue("--icon-size")) ||
        Number.parseFloat(iconStyles.width) ||
        20
      );
    }

    #measureLongestOverlayWidth() {
      const probe = this.#overlay.cloneNode(true);

      if (!(probe instanceof HTMLElement)) return 0;

      const overlayStyles = window.getComputedStyle(this.#overlay);

      probe.removeAttribute("hidden");
      probe.setAttribute("aria-hidden", "true");
      probe.style.inset = "auto";
      probe.style.left = "0";
      probe.style.maxWidth = "none";
      probe.style.pointerEvents = "none";
      probe.style.position = "absolute";
      probe.style.top = "0";
      probe.style.visibility = "hidden";
      probe.style.width = "max-content";
      probe.style.fontFamily = overlayStyles.fontFamily;
      probe.style.fontSize = overlayStyles.fontSize;
      probe.style.fontWeight = overlayStyles.fontWeight;
      probe.style.letterSpacing = overlayStyles.letterSpacing;
      probe.style.gap = overlayStyles.gap;

      const typewriter = probe.querySelector(
        "[data-animated-search-text-typewriter]",
      );

      if (!(typewriter instanceof HTMLElement)) return 0;

      // Undo live ellipsis/shrink so the probe reports full phrase width.
      typewriter.style.flex = "0 0 auto";
      typewriter.style.minWidth = "max-content";
      typewriter.style.overflow = "visible";
      typewriter.style.textOverflow = "clip";

      // Prefer the field when laid out; otherwise measure on body so hidden
      // stacked/compact/mobile search still publishes --header-search-min-width.
      const host = this.#canMeasureInField() ? this.#field : document.body;

      host?.appendChild(probe);

      let textWidth = 0;

      for (const phrase of this.#phrases) {
        typewriter.textContent = phrase;
        textWidth = Math.max(textWidth, Math.ceil(probe.scrollWidth));
      }

      probe.remove();

      return textWidth;
    }

    #canMeasureInField() {
      if (!this.#field || !this.#desktopForm) return false;
      if (this.#field.closest("[hidden]")) return false;

      const style = window.getComputedStyle(this.#desktopForm);

      return style.display !== "none" && style.visibility !== "hidden";
    }

    #clearMinWidth() {
      this.#field?.style.removeProperty("--header-search-field-min-width");
      this.#desktopForm?.style.removeProperty("--header-search-min-width");
      this.#measuredFor = "";
    }
  }

  customElements.define(COMPONENT_NAME, AnimatedSearchText);
})();
